if (!customElements.get("variant-options")) {
  // rAF alone can be starved in hidden tabs, so the timeout guarantees the
  // deferred work still runs.
  const afterNextPaint = () =>
    new Promise((resolve) => {
      let done = false;
      const run = () => {
        if (done) return;
        done = true;
        resolve();
      };
      requestAnimationFrame(() => setTimeout(run, 0));
      setTimeout(run, 150);
    });
  const yieldToMain = () => new Promise((resolve) => setTimeout(resolve, 0));

  customElements.define(
    "variant-options",
    class VariantOptions extends HTMLElement {
      whenLoaded = Promise.all([customElements.whenDefined("gallery-section")]);
      constructor() {
        super();
        this.addEventListener("change", this.onVariantChange);
        this.addEventListener("keydown", this.onKeyDown);

        this.container = document.querySelector(
          `section[data-product-handle="${this.getAttribute("data-product-handle")}"]`,
        );
      }

      connectedCallback() {
        this.whenLoaded.then(() => {
          this.initialize();
        });
      }

      disconnectedCallback() {}

      initialize() {
        this.updateOptions();
        this.updateMasterId();
        this.updateGallery();
        this.updateVariantStatuses();
        this.updateDropdownButtons();
      }

      onKeyDown(event) {
        if (event.key === "Enter" || event.keyCode === 13) {
          const input = event.target.querySelector("input");
          input?.click();
          // this.onVariantChange();
        }
      }

      async onVariantChange() {
        const variantChangeStartEvent = new CustomEvent("variantChangeStart", {
          bubbles: true,
          composed: true,
        });
        this.dispatchEvent(variantChangeStartEvent);

        // Only what the shopper sees in the next frame (selected label, price,
        // add-to-cart state, hidden variant id) runs inside the input event.
        this.updateOptions();
        this.updateMasterId();
        this.removeErrorMessage();

        const variant = this.currentVariant;
        if (!variant) {
          this.toggleAddButton(true, "", true);
          this.setUnavailable();
        } else {
          this.toggleAddButton(
            !variant.available,
            variant.available ? "" : window.variantStrings.soldOut,
          );
          this.updateVariantInput();
          this.renderProductInfo(false);
        }

        // Gallery rebuild, availability matrix and listeners of
        // variantChangeEnd are expensive; run them after the frame is painted
        // and drop stale runs when the shopper clicks again in between.
        const token = (this.variantChangeToken = (this.variantChangeToken || 0) + 1);
        await afterNextPaint();
        if (token !== this.variantChangeToken) return;

        this.updateGallery();

        await yieldToMain();
        if (token !== this.variantChangeToken) return;

        this.updatePickupAvailability();
        this.updateVariantStatuses();
        this.updateDropdownButtons();

        if (variant) {
          this.updateMedia();
          if (document.querySelectorAll("variant-options").length === 1) {
            this.updateURL();
          }
          this.updateShareUrl();
          this.publishVariantChange();
        }

        await yieldToMain();
        if (token !== this.variantChangeToken) return;

        const variantChangeEndEvent = new CustomEvent("variantChangeEnd", {
          bubbles: true,
          composed: true,
        });
        this.dispatchEvent(variantChangeEndEvent);
      }

      updateGallery() {
        const mediaGallery = document.getElementById(
          `MediaGallery-${this.dataset.section}`,
        );

        let media_id = false;
        if (this.currentVariant && this.currentVariant.featured_media) {
          media_id = this.currentVariant.featured_media.id;
        }

        try{
          mediaGallery?.filterSlides(this.options, media_id, true);
        } catch (error) {}
      }

      updateDropdownButtons(){
        const fieldsets = Array.from(
          this.querySelectorAll(".wt-product__option"),
        );
        this.options = fieldsets.map((fieldset) => {
          return Array.from(fieldset.querySelectorAll("input")).find(
            (radio) => radio.checked,
          )?.value || '';
        });

        fieldsets.forEach((fieldset) => {
          const dropdown = fieldset.querySelector(
            ".wt-product__option__dropdown",
          );
          if (dropdown) {
            const checkedInput = fieldset.querySelector('fieldset input:checked')
            if(!checkedInput) return
            const isInputDisabled = checkedInput?.classList.contains('disabled')
            dropdown.classList.toggle('wt-product__option__dropdown--unavailable', isInputDisabled)
          }
        });
      }

      updateOptions() {
        const fieldsets = Array.from(
          this.querySelectorAll(".wt-product__option"),
        );
        this.options = fieldsets.map((fieldset) => {
          return (
            Array.from(fieldset.querySelectorAll("input")).find(
              (radio) => radio.checked,
            )?.value || ""
          );
        });

        this.checkedOptions = fieldsets.map((fieldset) => {
          return Array.from(fieldset.querySelectorAll("input")).find(
            (radio) => radio.checked,
          ) || '';
        });

        fieldsets.forEach((fieldset, index) => {
          const selectedOption = this.options[index];

          if (!selectedOption) return;

          const titleValue = fieldset.querySelector(
            ".wt-product__option__title .value",
          );
          if (titleValue) titleValue.innerHTML = selectedOption;
          const dropdownSpan = fieldset.querySelector(
            ".wt-product__option__dropdown span",
          );
          if (dropdownSpan) dropdownSpan.innerHTML = selectedOption;
        });
      }

      updateMasterId() {
        this.currentVariant = this.getVariantData()?.find((variant) => {
          return !variant.options
            .map((option, index) => {
              return this.options[index] === option;
            })
            .includes(false);
        });
      }

      updateMedia() {
        if (!this.currentVariant) return;
        this.setAttribute("data-variant-id", this.currentVariant?.id);
        if (!this.currentVariant.featured_media) return;
        this.setAttribute(
          "data-featured-image",
          this.currentVariant?.featured_media?.preview_image?.src,
        );
        this.setAttribute(
          "data-featured-image-id",
          this.currentVariant?.featured_media?.id,
        );

        const modalContent = document.querySelector(
          `#ProductModal-${this.dataset.section} .product-media-modal__content`,
        );
        if (!modalContent) return;
        const newMediaModal = modalContent.querySelector(
          `[data-media-id="${this.currentVariant.featured_media.id}"]`,
        );
        modalContent.prepend(newMediaModal);
      }

      updateURL() {
        if (!this.currentVariant || this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${this.dataset.url}?variant=${this.currentVariant.id}`,
        );
      }

      updateShareUrl() {
        const shareButton = document.getElementById(
          `Share-${this.dataset.section}`,
        );
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(
          `${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`,
        );
      }

      updateVariantInput() {
        const productForms = document.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        );
        productForms.forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = this.currentVariant.id;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateVariantStatuses() {
        // const selectedOptionOneVariants = this.variantData.filter(
        //   (variant) => this.querySelector(':checked').value === variant.option1
        //   );
        const selectedOptionOneVariants = this.variantData?.filter(
          (variant) => variant.available === true,
        );
        const inputWrappers = [
          ...this.querySelectorAll(".product-form__input"),
        ];

        const checkedInputs = [...this.querySelectorAll('.product-form__input :checked')]
        const checkedInputsValues = [...this.querySelectorAll('.product-form__input :checked')].map(input => input.getAttribute('value'))

        const previousSelectedOptions = []
        inputWrappers.forEach((option, index) => {
          if (index === 0 && this.currentVariant?.options.length > 1) return;

          const optionInputs = [
            ...option.querySelectorAll('input[type="radio"], option'),
          ];
          const previousOptionSelected =
            inputWrappers[index - 1]?.querySelector(":checked")?.value;
          
            previousSelectedOptions.push(previousOptionSelected)

              const availableOptionInputsValue = selectedOptionOneVariants
              .filter(
                (variant) => {
                  if (index === 2){
                    return variant.available &&
                    variant[`option1`] === previousSelectedOptions[0] &&
                    variant[`option2`] === previousSelectedOptions[1]
                  } else {
                    return variant.available &&
                    variant[`option${index}`] === previousOptionSelected
                  }
                }
              )
              .map((variantOption) => variantOption[`option${index + 1}`]);
              this.setInputAvailability(optionInputs, availableOptionInputsValue, checkedInputsValues, index, checkedInputs);
        });
      }

      setInputAvailability(listOfOptions, listOfAvailableOptions, checkedInputsValues, index, checkedInputs) {
        // Selected values must match the same leading option positions exactly.
        function hasOptionPrefix(options, selectedOptions) {
          return selectedOptions.every(
            (value, optionIndex) => options[optionIndex] === value,
          );
        }
        
        const checkedValues = checkedInputsValues.slice(0, index)

        const listOfAvailableVariants= this.getVariantData().filter(variant => {
            if(hasOptionPrefix(variant.options, checkedValues)) return true
        })

        const isPreviousOptionChecked = checkedInputs.find(input => input.dataset.position === `${index}`) || index === 0
        

        listOfOptions.forEach((input) => {          
          if(!isPreviousOptionChecked) return;
          if (listOfAvailableOptions.includes(input.getAttribute("value"))) {
            input.classList.remove("disabled");
          } else {
            input.classList.add("disabled");
          }

          // check if option exist. If not, it should be disabled
          let inputOccurs = false;
          listOfAvailableVariants.forEach(variant => {
            if(variant.options.includes(input.getAttribute("value"))) {
              inputOccurs = true
            }
          })

          if(!inputOccurs) input.classList.add("disabled");

        });
      }

      updatePickupAvailability() {
        const pickUpAvailability = document.querySelector(
          "pickup-availability",
        );
        if (!pickUpAvailability) return;

        pickUpAvailability.dataset.variantId = this.currentVariant?.id;
        if (this.currentVariant && this.currentVariant.available) {
          pickUpAvailability.fetchAvailability(this.currentVariant.id);
        } else {
          pickUpAvailability.removeAttribute("available");
          pickUpAvailability.innerHTML = "";
        }
      }

      removeErrorMessage() {
        const section = this.closest("section");
        if (!section) return;

        const productForm = section.querySelector("product-form");
        try {
          productForm?.handleErrorMessage();
        } catch (err) {
          console.log(err);
        }
      }

      renderProductInfo(shouldPublish = true) {
        const variantUi = this.getVariantUiData()?.variants?.[
          this.currentVariant.id
        ];

        this.updatePrice(variantUi);
        this.updateSku(variantUi);
        this.updateInventory(variantUi);

        if (shouldPublish) this.publishVariantChange();
      }

      publishVariantChange() {
        if (!this.currentVariant) return;
        const sectionId = this.dataset.originalSection || this.dataset.section;
        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId,
            variant: this.currentVariant,
          },
        });
      }

      updatePrice(variantUi) {
        const destination = document.getElementById(
          `price-${this.dataset.section}`,
        );
        const price = destination?.querySelector(".price");
        if (!destination || !price || !variantUi) return;

        const hasSale = Boolean(variantUi.compareAtPrice);
        price.classList.toggle("price--sold-out", !this.currentVariant.available);
        price.classList.toggle("price--on-sale", hasSale);

        const regularPrice = price.querySelector(
          ".price__regular .price-item--regular",
        );
        const sale = price.querySelector(".price__sale");
        const salePrice = sale?.querySelector(".price-item--last");
        const comparePrice = sale?.querySelector("s.price-item--regular");

        if (regularPrice) regularPrice.textContent = variantUi.price;
        if (salePrice) salePrice.textContent = variantUi.price;
        if (comparePrice) {
          comparePrice.textContent = variantUi.compareAtPrice || "";
          comparePrice.classList.toggle("hidden", !hasSale);
        }
        sale?.classList.toggle("visible", hasSale);
        destination.classList.remove("visibility-hidden");
      }

      updateSku(variantUi) {
        const destination = document.getElementById(`Sku-${this.dataset.section}`);
        if (!destination) return;

        const sku = variantUi?.sku || "";
        destination.classList.toggle("visibility-hidden", !sku);
        if (!sku) return;

        const label = destination.querySelector(".visually-hidden")?.cloneNode(true);
        destination.replaceChildren();
        if (label) destination.appendChild(label);
        destination.append(`SKU:${sku}`);
      }

      updateInventory(variantUi) {
        const destination = document.getElementById(
          `Inventory-${this.dataset.section}`,
        );
        if (!destination) return;

        const isTracked = variantUi?.inventoryManagement === "shopify";
        destination.classList.toggle("visibility-hidden", !isTracked);
        if (!isTracked) return;

        const quantity = Number(variantUi.inventoryQuantity || 0);
        const threshold = Number(destination.dataset.inventoryThreshold || 0);
        const showQuantity = destination.dataset.showInventoryQuantity === "true";
        const messages = this.getVariantUiData()?.inventoryMessages || {};
        let color = "rgb(62,214,96)";
        let colorWithOpacity = "rgb(62,214,96, 0.3)";
        let message;

        if (quantity > 0 && quantity <= threshold) {
          color = "rgb(238,148,65)";
          colorWithOpacity = "rgb(238,148,65, 0.3)";
          message = showQuantity
            ? messages.lowStockCount?.replace("[quantity]", quantity)
            : messages.lowStock;
        } else if (quantity > 0) {
          message = showQuantity
            ? messages.inStockCount?.replace("[quantity]", quantity)
            : messages.inStock;
        } else if (variantUi.inventoryPolicy === "continue") {
          message = messages.continueSelling;
        } else {
          color = "rgb(200,200,200)";
          colorWithOpacity = "rgb(200,200,200, 0.3)";
          message = messages.outOfStock;
        }

        destination.innerHTML = `<svg width="15" height="15" aria-hidden="true"><circle cx="7.5" cy="7.5" r="7.5" fill="${colorWithOpacity}"/><circle cx="7.5" cy="7.5" r="5" stroke="rgb(255, 255, 255)" stroke-width="1" fill="${color}"/></svg>`;
        destination.append(message || "");
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(
          `product-form-${this.dataset.section}`,
        );
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute("disabled", "disabled");
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute("disabled");
          addButtonText.textContent = window.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        const button = document.getElementById(
          `product-form-${this.dataset.section}`,
        );
        const addButton = button.querySelector('[name="add"]');
        const addButtonText = button.querySelector('[name="add"] > span');
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(
          `Inventory-${this.dataset.section}`,
        );
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (!addButton) return;
        addButtonText.textContent = window.variantStrings.unavailable;
        if (price) price.classList.add("visibility-hidden");
        if (inventory) inventory.classList.add("visibility-hidden");
        if (sku) sku.classList.add("visibility-hidden");
      }

      getVariantData() {
        this.variantData =
          this.variantData ||
          JSON.parse(
            this.querySelector('[type="application/json"]').textContent,
          );
        return this.variantData;
      }

      getVariantUiData() {
        this.variantUiData =
          this.variantUiData ||
          JSON.parse(
            this.querySelector("[data-variant-ui-data]")?.textContent || "{}",
          );
        return this.variantUiData;
      }
    },
  );
}
// customElements.define('variant-options', VariantOptions);
